import { IBaseUser, IGroupUser, IFullGroupUser } from '../../../../typings/model/user';
import { DB } from '../../../../typings/model/db';
import { GroupUserInfo, UserType } from '../../../../typings/proto';
import { TUserListMap, TFilterUserListMap } from '../typings';
import { UserManager } from '../../user';
import { getFormatDBUser } from '../../simple-user/util';

/**
 * 获取分类的用户列表
 * @param dataList 用户列表原始数据
 * @returns 分类后的列表数据
 */
export function getFilterUserList(dataList: IGroupUser[]): TFilterUserListMap {
  const userMap: Record<string, IGroupUser> = {};
  const userList = [];
  const botList = [];
  for (const data of dataList) {
    const { uid, isBot } = data;
    if (userMap[uid]) {
      continue;
    }

    if (isBot) {
      botList.push(data);
    } else {
      userList.push(data);
    }
    userMap[uid] = data;
  }

  return {
    userList,
    botList,
  };
}

/**
 * 获取格式化的baseUser
 * @param data 接口拉取回来的groupUserInfo
 * @returns 格式化后的baseUser
 */
function getFormatBaseUser(data: GroupUserInfo): IBaseUser {
  const { userInfo, onlineStatus, hasRoleCard, profile, botInfo } = data;

  return {
    ...userInfo,
    profile,
    hasRoleCard,
    onlineStatus: onlineStatus?.status,
    botPublicType: botInfo?.publicType,
  };
}

/**
 * 获取格式化的groupUser
 * @param data 接口拉取回来的groupUserInfo
 * @returns 格式化后的groupUser
 */
export function getFormatGroupUser(data: GroupUserInfo): IGroupUser {
  const { userInfo, showNickSpell } = data;
  const sortTag = (showNickSpell || '').toUpperCase();
  // 非字母的标志位首字母，聚类在'#'
  let sortTagInitial = sortTag.slice(0, 1);
  if (!/^[A-Z]/.test(sortTagInitial)) {
    sortTagInitial = '#';
  }

  return {
    uid: userInfo?.uid,
    sortTag,
    sortTagInitial,
    roleId: data.roleId,
    roleName: data.roleName,
    roleIdList: data.roleIdList,
    groupNickname: data.showNick,
    isBot: userInfo?.userType === UserType.UserTypeBot,
  };
}

/**
 * 获取格式化后的用户列表映射
 * @param groupId 群id
 * @param groupChannelKey 群组频道的key
 * @param dataList 接口拉取回来的user列表数据
 * @returns 格式化后的用户列表映射
 */
export async function getFormatUserListMap(
  groupId: string,
  groupChannelKey: string,
  dataList: GroupUserInfo[],
): Promise<TUserListMap> {
  const baseUserList: IBaseUser[] = [];
  const groupUserList: IGroupUser[] = [];
  const dbUserList: DB.IUserInfo[] = [];
  for (const data of dataList) {
    baseUserList.push(getFormatBaseUser(data));
    groupUserList.push(getFormatGroupUser(data));
    const dbUser = await getFormatDBUser(data, groupId, groupChannelKey);
    dbUserList.push(dbUser);
  }

  return {
    baseUserList,
    groupUserList,
    dbUserList,
  };
}

/**
  * 获取完整用户信息列表
  * @param groupUserList 群用户信息列表
  * @returns 完整用户信息列表
  */
export function getFullGroupUserList(groupUserList: IGroupUser[]): IFullGroupUser[] {
  const fullGroupUserList = [];
  for (const item of groupUserList) {
    const { uid } = item;
    const baseUser = UserManager.getInstance().getUser(uid);
    if (!baseUser) {
      continue;
    }
    fullGroupUserList.push({
      ...baseUser,
      ...item,
    });
  }

  return fullGroupUserList;
}
